import { useState } from 'react';

import type { SelectedFilterValue } from '../../types';
import { useFilterState } from '../../hooks/useFilterState';

export type DialogControls<T> = {
  isOpen: boolean;
  open: (value: T) => void;
  close: () => void;
};

export type NestedDialogControlsV2 = {
  filterDialog: DialogControls<never>;
  detailsDialog: DialogControls<string>;
  sortingDialog: DialogControls<string>;
  displayModeDialog: DialogControls<string>;
  closeAllDialogs: () => void;
};

/** Represents the state and logic of having a two-stage filter selection, selecting a filter in the outer dialog and individual filter values in the inner dialog */
export function useNestedFilterDialogsV2({
  initiallySelectedFilterValues = [],
}: {
  initiallySelectedFilterValues?: SelectedFilterValue[];
} = {}) {
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [isSortingDialogOpen, setIsSortingDialogOpen] = useState(false);
  const [isDisplayModeDialogOpen, setIsDisplayModeDialogOpen] = useState(false);

  const {
    selectedFilterId,
    selectFilter,
    pendingFilterValues,
    toggleFilterValue,
    setFilterRange,
    activeFilterIds,
    hasChanges,
  } = useFilterState(initiallySelectedFilterValues);

  const isDetailsDialogOpen = isFilterDialogOpen && !!selectedFilterId;

  const openDetailsDialog = (id: string) => {
    selectFilter(id);
  };

  const openFilterDialog = () => {
    setIsFilterDialogOpen(true);
  };

  const closeFilterDialog = () => {
    setIsFilterDialogOpen(false);
  };

  const closeDetailsDialog = () => {
    selectFilter(undefined);
  };

  const openSortingDialog = () => {
    setIsSortingDialogOpen(true);
  };

  const closeSortingDialog = () => {
    setIsSortingDialogOpen(false);
  };

  const openDisplayModeDialog = () => {
    setIsDisplayModeDialogOpen(true);
  };

  const closeDisplayModeDialog = () => {
    setIsDisplayModeDialogOpen(false);
  };

  const closeAllDialogs = () => {
    closeDetailsDialog();
    closeFilterDialog();
    closeSortingDialog();
    closeDisplayModeDialog();
  };

  return {
    selectedFilterId,
    activeFilterIds,
    pendingFilterValues,
    hasFilterChanges: hasChanges,
    toggleFilterValue,
    setFilterRange,
    dialogControls: {
      filterDialog: {
        isOpen: isFilterDialogOpen,
        open: openFilterDialog,
        close: closeFilterDialog,
      },
      detailsDialog: {
        isOpen: isDetailsDialogOpen,
        open: openDetailsDialog,
        close: closeDetailsDialog,
      },
      sortingDialog: {
        isOpen: isSortingDialogOpen,
        open: openSortingDialog,
        close: closeSortingDialog,
      },
      displayModeDialog: {
        isOpen: isDisplayModeDialogOpen,
        open: openDisplayModeDialog,
        close: closeDisplayModeDialog,
      },
      closeAllDialogs,
    } satisfies NestedDialogControlsV2,
  };
}
